import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchTopupUser, sendWalletPayment } from "../services/api";
import PaymentSuccessModal from "../components/PaymentSuccessModal";
import PaymentFailedModal from "../components/PaymentFailedModal";
import { Eye, EyeOff } from "lucide-react";
import { fetchUserById } from "../services/api";
import Webcam from "react-webcam";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
const SELFIE_FAIL_TTL = 12 * 60 * 60 * 1000;
const TopupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const receiverId = searchParams.get("user");
  const [receiver, setReceiver] = useState(null);
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [failedInfo, setFailedInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [requirePassword, setRequirePassword] = useState(false);
  const [selfieLoading, setSelfieLoading] = useState(false);
  const [selfieFailCount, setSelfieFailCount] = useState(0);
  const [userData, setUserData] = useState(null);
  const [isFaceVerified, setIsFaceVerified] = useState(false);
  // 🔐 SmartSelfie
  const [showSelfie, setShowSelfie] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [facingMode, setFacingMode] = useState("user");
  const [isMobile, setIsMobile] = useState(false);

  const cameraRef = useRef(null);
  const pendingPaymentRef = useRef(null);
  const isFaceVerifiedFromUrl = searchParams.get("isFaceVerified") === "true";
  console.log('isFaceVerifiedFromUrl', isFaceVerifiedFromUrl);
  
  /* =========================
     LOAD RECEIVER
  ========================= */
  const authUserId = localStorage.getItem("userId");
  const FAIL_COUNT_KEY = `selfieFailCount_${authUserId}`;
  const FAIL_TIME_KEY = `selfieFailTimestamp_${authUserId}`;
  const webcamRef = useRef(null);

  useEffect(() => {
    if (isFaceVerifiedFromUrl) {
      setIsFaceVerified(true);
    }
  }, [isFaceVerifiedFromUrl]);

  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0;

    const isSmallScreen = window.innerWidth <= 768;

    setIsMobile(isTouchDevice && isSmallScreen);
  }, []);

  const toggleCamera = () => {
    setFacingMode((prev) =>
      prev === "user" ? "environment" : "user"
    );
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const storedUserId = localStorage.getItem("userId");

    if (!receiverId) return;
    const loadUser = async () => {
      try {
        const data = await fetchUserById(storedUserId, token);
        setUserData(data);
        if (data?.is_smile_id_blocked === 1) {
          setRequirePassword(true);
          setShowPassword(true);
          setSelfieFailCount(2); // hard-stop SmartSelfie
        }
      } catch (error) {
        console.error("Fetch user failed:", error);
        if (
          error.message.includes("Unauthenticated") ||
          error.message.includes("401")
        ) {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("userId");
          localStorage.removeItem("roleId");
          navigate("/login");
        }
      }
    };
    loadUser();

    setIsLoading(true);
    fetchTopupUser(receiverId)
      .then((res) => {
        setReceiver(res.user);
      })
      .catch((err) =>
        setMessage({
          text: err.message || "Failed to load recipient",
          type: "error",
        })
      )
      .finally(() => setIsLoading(false));
  }, [receiverId]);

  /* =========================
     SEND PAYMENT (STEP 1)
  ========================= */
  const handlePayment = (e) => {
    e.preventDefault();

    if (!amount || isNaN(amount) || amount <= 0) {
      setMessage({ text: "Please enter a valid amount", type: "error" });
      return;
    }


    pendingPaymentRef.current = {
      receiver_id: receiverId,
      amount: parseFloat(amount),
      password,
      note,
    };

    if (requirePassword || userData?.is_smile_id_blocked == 1) {
      if (!password) {
        setMessage({ text: "Please enter your password", type: "error" });
        return;
      }
      // 🚀 Password flow → DIRECT PAYMENT
      completePayment();
      return;
    } else {
       if (isFaceVerified) {
        completePayment();
        return;
      }
      // 🔐 First attempt → SELFIE AUTH
      if (selfieFailCount >= 3) {
        completePayment();
      } else {
        setShowSelfie(true);
      }
    }
  };

  useEffect(() => {
    if (isFaceVerified) {
      setShowSelfie(false);
      setRequirePassword(false);
      setShowPassword(false);
    }
  }, [isFaceVerified]);

  useEffect(() => {
    if (userData?.is_smile_id_blocked === 1) {
      setShowSelfie(false);
    }
  }, [userData]);

  useEffect(() => {
    if (!authUserId) return;

    const now = Date.now();
    const storedCount = Number(localStorage.getItem(FAIL_COUNT_KEY)) || 0;
    const storedTime = Number(localStorage.getItem(FAIL_TIME_KEY)) || 0;

    if (!storedTime || now - storedTime > SELFIE_FAIL_TTL) {
      localStorage.removeItem(FAIL_COUNT_KEY);
      localStorage.removeItem(FAIL_TIME_KEY);
      setSelfieFailCount(0);
      setRequirePassword(false);
    } else {
      setSelfieFailCount(storedCount);
      if (storedCount >= 3) {
        setRequirePassword(true);
      }
    }
  }, [authUserId]);


  /* =========================
     SMART SELFIE HANDLER
  ========================= */
  useEffect(() => {
    if (!showSelfie) return;

    let cameraEl;

    // const handlePublish = async (event) => {
    //   if (selfieLoading) return;
    //   try {
    //     setSelfieLoading(true);
    //     const payload = event.detail;
    //     const token = localStorage.getItem("adminToken");

    //     const response = await fetch(
    //       `${API_BASE_URL}/smile/smartselfie/authentication`,
    //       {
    //         method: "POST",
    //         headers: {
    //           "Content-Type": "application/json",
    //           Authorization: `Bearer ${token}`,
    //         },
    //         body: JSON.stringify(payload),
    //       }
    //     );
    //     const rdata = await response.json();
    //     console.log('response', rdata);
    //     if (!rdata.job_success) {
    //       const newFailCount = selfieFailCount + 1;
    //       setSelfieFailCount(newFailCount);
    //       localStorage.setItem("selfieFailCount", newFailCount);
    //       if (newFailCount >= 2) {
    //         setRequirePassword(true);
    //         setMessage({
    //           text: "Face verification failed. Please verify using password.",
    //           type: "error",
    //         });
    //       }
    //       throw new Error("Face verification failed");
    //     }
    //     setShowSelfie(false);
    //     setSelfieFailCount(0);
    //     completePayment();
    //   } catch (err) {
    //     setShowSelfie(false);
    //     console.error("SmartSelfie error:", err);
    //     setShowFailedModal(true);
    //     // alert("Face verification failed");
    //   } finally {
    //       setSelfieLoading(false);
    //   }
    // };

    const handlePublish = async (event) => {
      if (selfieLoading) return;
      const now = Date.now();
      try {
        setSelfieLoading(true);

        const payload = event.detail;
        const token = localStorage.getItem("adminToken");

        const response = await fetch(
          `${API_BASE_URL}/smile/smartselfie/authentication`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        const rdata = await response.json();
        console.log("SmartSelfie response:", rdata);

        /* ❌ FACE VERIFICATION FAILED */
        if (!rdata.job_success) {
          const prevCount = Number(localStorage.getItem(FAIL_COUNT_KEY)) || 0;
          const firstFailTime =
            Number(localStorage.getItem(FAIL_TIME_KEY)) || now;

          if (now - firstFailTime > SELFIE_FAIL_TTL) {
            localStorage.setItem(FAIL_COUNT_KEY, 1);
            localStorage.setItem(FAIL_TIME_KEY, now);
            setSelfieFailCount(1);
          } else {
            const newCount = prevCount + 1;
            localStorage.setItem(FAIL_COUNT_KEY, newCount);
            localStorage.setItem(FAIL_TIME_KEY, firstFailTime);
            setSelfieFailCount(newCount);

            if (newCount <= 2) {
              setMessage({
                text: "Face verification failed. Please try again.",
                type: "error",
              });
            }

            if (newCount >= 3) {
              setRequirePassword(true);
              setMessage({
                text: "Face verification failed. Please verify using password.",
                type: "error",
              });
            }
          }
          throw new Error("Face verification failed");
        }

        /* ✅ FACE VERIFICATION SUCCESS */
        localStorage.removeItem("selfieFailCount");
        localStorage.removeItem("selfieFailTimestamp");
        localStorage.removeItem(FAIL_COUNT_KEY);
        localStorage.removeItem(FAIL_TIME_KEY);
        setSelfieFailCount(0);
        setRequirePassword(false);
        setShowPassword(false);

        setShowSelfie(false);
        completePayment();

      } catch (err) {
        console.error("SmartSelfie error:", err);
        setShowSelfie(false);
      } finally {
        // ALWAYS stop loader
        setSelfieLoading(false);
      }
    };


    const init = async () => {
      await loadSmileScript();
      cameraEl = cameraRef.current;
      cameraEl?.addEventListener(
        "smart-camera-web.publish",
        handlePublish
      );
    };

    init();

    return () => {
      cameraEl?.removeEventListener(
        "smart-camera-web.publish",
        handlePublish
      );
    };
  }, [showSelfie]);

  /* =========================
     PAYMENT (STEP 2)
  ========================= */
  const completePayment = async () => {
    setPaymentLoading(true);

    try {
      const res = await sendWalletPayment(pendingPaymentRef.current);

      setPaymentInfo({
        amount,
        receiverName: receiver?.name,
        time: new Date().toLocaleString(),
        txnId: res?.txnId || null,
      });
      setRequirePassword(false);
      setShowPassword(false);
      setSelfieFailCount(0);
      setPassword("");
      setMessage({ text: "", type: "" });

      localStorage.removeItem("selfieFailCount");
      localStorage.removeItem("selfieFailTimestamp");
      localStorage.removeItem(FAIL_COUNT_KEY);
      localStorage.removeItem(FAIL_TIME_KEY);
      setShowSuccessModal(true);
      setTimeout(() => navigate("/user-profile"), 4000);
    } catch (err) {
      setFailedInfo({
        amount,
        receiverName: receiver?.name,
        time: new Date().toLocaleString(),
        message:
          err.message ||
          "Payment failed. Please check balance or password.",
      });

      setShowFailedModal(true);
      setTimeout(() => navigate("/user-profile"), 4000);
    } finally {
      setPaymentLoading(false);
    }
  };

  const captureAndVerify = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setError("Camera access required to continue");
      setLoading(false);
      return;
    }

    const blob = await fetch(imageSrc).then((res) => res.blob());
    const file = new File([blob], "face.jpg", { type: "image/jpeg" });

    const formData = new FormData();
    formData.append("image", file);
    formData.append("receiver_id", receiverId);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("adminToken");

    if (!token) {
      setError("Session expired. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/user/face-verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (!res.ok) {
        setError(data.message || "Face verification failed");
      } else {
        completePayment();
        setSuccess("Face verified successfully");
        // setUserData(data.user);     // 👈 store user data
      }
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  /* =========================
     UI
  ========================= */
  return (
    <div className="topup-wrapper">
      <div className="topup-card qr-card card border-0 shadow-sm mx-auto theme-card-white">
        <div className="text-center mb-4">
          <h2 className="fw-semibold theme-text-black pb-1">Wallet Payment</h2>
          <p className="text-muted text-sm mb-0 theme-text-color">Enter the amount to make a payment.</p>
        </div>
        {isLoading ? (
          <p className="text-muted">Loading recipient...</p>
        ) : receiver ? (
          <>
            <div className="receiver-box">
              <span>You are sending money to</span>
              <strong>{receiver.name}</strong>
            </div>

            <form onSubmit={handlePayment} className="topup-form">
              <div>
                <label className="form-label">Amount (₹)</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
              {requirePassword && (
                <>
                  <div>
                    <label className="form-label">Password</label>
                    <div className="password-field">
                      <input
                        className="form-control"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <span onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                      </span>
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="form-label">Note</label>
                <input
                  className="form-control"
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              {message.text && (
                <p className={`msg ${message.type}`}>{message.text}</p>
              )}

              <button type="submit" disabled={paymentLoading} className="theme-btn justify-content-center">
                {paymentLoading ? "Processing..." : "Send Payment"}
              </button>
            </form>
          </>
        ) : (
          <p className="msg error">Recipient not found</p>
        )}
      </div>

      {/* 🔐 SMART SELFIE */}
      {showSelfie && !isFaceVerified && (
        <div className="selfie-overlay">
          <div className="selfie-card">
            <h3 className="selfie-title">Face Verification</h3>
            <p className="selfie-subtitle">
              Please look into the camera to continue
            </p>

            <div className="selfie-camera-wrapper">
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: facingMode,
                  width: 720,
                  height: 720,
                }}
                className="selfie-webcam"
              />

              {loading && (
                <div className="loader-overlay">
                  <div className="spinner"></div>
                  <span>Verifying…</span>
                </div>
              )}
            </div>

            {error && <div className="selfie-error">{error}</div>}
            {success && <div className="selfie-success">{success}</div>}

            {isMobile && (
              <button
                onClick={toggleCamera}
                disabled={loading}
                style={{
                  // ...styles.button,
                  background: "#0ea5e9",
                  marginTop: 12,
                }}
                className="selfie-primary-btn"
              >
                Switch Camera
              </button>
            )}

            <button
              className="selfie-primary-btn"
              onClick={captureAndVerify}
              disabled={loading || success}
            >
              {loading ? "Verifying…" : success ? "Verified" : "Verify Face"}
            </button>

            <button
              className="selfie-cancel-btn"
              onClick={() => setShowSelfie(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <PaymentSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        payload={paymentInfo || {}}
      />

      <PaymentFailedModal
        open={showFailedModal}
        onClose={() => setShowFailedModal(false)}
        payload={failedInfo || {}}
      />

      {/* ===== STYLES ===== */}
      <style>{`
        .topup-wrapper {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f7fb;
        }
        .topup-card {
          background: #fff;
          width: 420px;
          max-width: 95%;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 15px 40px rgba(0,0,0,.12);
        }
        .topup-title {
          text-align: center;
          margin-bottom: 16px;
        }
        .receiver-box {
          background: #f1f5f9;
          padding: 12px;
          border-radius: 10px;
          margin-bottom: 16px;
          text-align: center;
        }
        .topup-form label {
          font-size: 14px;
          margin-top: 12px;
          display: block;
        }
        .topup-form input {
          width: 100%;
          padding: 10px;
          margin-top: 6px;
          border-radius: 8px;
          border: 1px solid #ddd;
        }
        .password-field {
          position: relative;
        }
        .password-field span {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
        }
        .msg.error {
          color: #dc2626;
          margin-top: 8px;
        }

        .selfie-overlay {
          position: fixed;
          inset: 0;
          background: rgba(255,255,255,.8);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        .selfie-card {
          background: white;
          width: 420px;
          max-width: 95%;
          padding: 20px;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 20px 50px rgba(0,0,0,.15);
        }
        .cancel-btn {
          margin-top: 12px;
          padding: 10px;
          width: 100%;
          border-radius: 8px;
          border: none;
          background: #f3f4f6;
          cursor: pointer;
        }
        .back-button{
          display: none;
        }
        .selfie-camera-wrapper {
          position: relative;
        }

        .loader-overlay {
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          z-index: 10;
        }

        .spinner {
          width: 42px;
          height: 42px;
          border: 4px solid #e5e7eb;
          border-top-color: rgb(var(--theme-color));
          border-radius: 50%;
          animation: spin 0.9s linear infinite;
        }

        .selfie-title {
          font-size: 20px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .selfie-subtitle {
          font-size: 13px;
          color: #64748b;
          margin-bottom: 16px;
        }

        .selfie-camera-wrapper {
          position: relative;
          width: 240px;
          height: 240px;
          margin: 0 auto 14px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid #4f46e5;
          background: #000;
        }

        .selfie-webcam {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .selfie-primary-btn {
          width: 100%;
          padding: 12px;
          border-radius: 10px;
          border: none;
          background: #4f46e5;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          margin-top: 8px;
          cursor: pointer;
        }

        .selfie-primary-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .selfie-cancel-btn {
          width: 100%;
          margin-top: 8px;
          padding: 10px;
          border-radius: 8px;
          border: none;
          background: #f1f5f9;
          color: #334155;
          font-size: 14px;
          cursor: pointer;
        }

        .selfie-error {
          background: #fee2e2;
          color: #991b1b;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          margin-top: 10px;
        }

        .selfie-success {
          background: #dcfce7;
          color: #166534;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          margin-top: 10px;
        }

        .loader-overlay span {
          margin-top: 8px;
          font-size: 13px;
          color: #1f2937;
        }


        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

/* =========================
   LOAD SMILE SCRIPT
========================= */
function loadSmileScript() {
  return new Promise((resolve, reject) => {
    if (window.customElements?.get("smart-camera-web")) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://cdn.smileidentity.com/js/v10/smart-camera-web.js";
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;

    document.body.appendChild(script);
  });
}

export default TopupPage;
